import React, {useCallback, useMemo} from 'react';
import {ShopifySubscription} from '../../interfaces';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridToolbar,
} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {Box, Paper, Typography} from '@mui/material';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';

interface Props {
  shopifySubscriptions: ShopifySubscription[];
  height?: number;
  loading?: boolean;
}

const formatDate = (value: unknown) =>
  value ? DateTime.fromISO(value as string).toFormat('yyyy-MM-dd') : '';

// Row categories, evaluated top-to-bottom (first match wins).
const ROW_CATEGORIES: {
  className: string;
  labelKey: string;
  color: string;
  matches: (sub: ShopifySubscription) => boolean;
}[] = [
  {
    className: 'sub-expired',
    labelKey: 'admin.shopifySubscriptionsTable.rowCategory.expired',
    color: 'rgb(244, 67, 54)',
    matches: sub => !!sub.expiredAt,
  },
  {
    className: 'sub-expiring',
    labelKey: 'admin.shopifySubscriptionsTable.rowCategory.expiringCanceled',
    color: 'rgb(255, 152, 0)',
    matches: sub => !!sub.expiringAt || !!sub.canceledAt,
  },
  {
    className: 'sub-paused',
    labelKey: 'admin.shopifySubscriptionsTable.rowCategory.paused',
    color: 'rgb(33, 150, 243)',
    matches: sub => !!sub.pauseStartDate,
  },
  {
    className: 'sub-unclaimed',
    labelKey: 'admin.shopifySubscriptionsTable.rowCategory.unclaimed',
    color: 'rgb(255, 235, 59)',
    matches: sub => !sub.isClaimed,
  },
  {
    className: 'sub-claimed',
    labelKey: 'admin.shopifySubscriptionsTable.rowCategory.claimed',
    color: 'rgb(76, 175, 80)',
    matches: sub => sub.isClaimed === true,
  },
];

const rowSx = ROW_CATEGORIES.reduce(
  (acc, {className, color}) => {
    acc[`& .${className}`] = {
      backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.2)'),
      '&:hover': {
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.3)'),
      },
    };
    return acc;
  },
  {} as Record<string, unknown>,
);

const getColumns = (t: TFunction): GridColDef[] => [
  {
    field: 'id',
    headerName: t('admin.shopifySubscriptionsTable.columns.id') as string,
    width: 90,
    filterable: true,
  },
  {
    field: 'email',
    headerName: t('general.email') as string,
    width: 280,
    filterable: true,
  },
  {
    field: 'status',
    headerName: t('general.status') as string,
    width: 130,
    filterable: true,
  },
  {
    field: 'plan',
    headerName: t('admin.shopifySubscriptionsTable.columns.plan') as string,
    width: 150,
    filterable: true,
  },
  {
    field: 'quantity',
    headerName: t('admin.shopifySubscriptionsTable.columns.quantity') as string,
    width: 100,
    filterable: true,
  },
  {
    field: 'isMonthly',
    headerName: t('admin.shopifySubscriptionsTable.columns.monthly') as string,
    width: 100,
    filterable: true,
  },
  {
    field: 'createdAt',
    headerName: t('admin.shopifySubscriptionsTable.columns.created') as string,
    width: 130,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
  {
    field: 'deliveredAt',
    headerName: t(
      'admin.shopifySubscriptionsTable.columns.delivered',
    ) as string,
    width: 130,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
  {
    field: 'canceledAt',
    headerName: t('admin.shopifySubscriptionsTable.columns.canceled') as string,
    width: 130,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
  {
    field: 'expiringAt',
    headerName: t('admin.shopifySubscriptionsTable.columns.expiring') as string,
    width: 130,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
  {
    field: 'expiredAt',
    headerName: t('admin.shopifySubscriptionsTable.columns.expired') as string,
    width: 130,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
];

const ShopifySubscriptionsTable: React.FC<Props> = ({
  shopifySubscriptions,
  height = 700,
  loading = false,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const columns = useMemo(() => getColumns(t), [t]);

  const handleRowClick = useCallback(
    (row: GridRowModel) => {
      navigate(`/admin/shopify-subscriptions/${row.id}`);
    },
    [navigate],
  );

  const getRowClassName = useCallback(
    ({row}: {row: ShopifySubscription}) =>
      ROW_CATEGORIES.find(category => category.matches(row))?.className ?? '',
    [],
  );

  return (
    <>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1}}>
        {ROW_CATEGORIES.map(({className, labelKey, color}) => (
          <Box
            key={className}
            sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                backgroundColor: color
                  .replace('rgb', 'rgba')
                  .replace(')', ', 0.5)'),
                border: `1px solid ${color}`,
              }}
            />
            <Typography variant="body2">{t(labelKey)}</Typography>
          </Box>
        ))}
      </Box>
      <Paper sx={{height}}>
        <DataGrid
          rows={shopifySubscriptions ?? []}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={getRowClassName}
          loading={loading}
          components={{Toolbar: GridToolbar}}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {debounceMs: 300},
            },
          }}
          disableSelectionOnClick
          sx={rowSx}
        />
      </Paper>
    </>
  );
};

export default React.memo(ShopifySubscriptionsTable);
